/** Formatting shared by the capture pages. Locale is fixed so the copy matches the design. */

const UNITS = ["bytes", "KB", "MB", "GB"] as const;

/** "142 MB", the way the handoff writes it — one decimal only where it earns its place. */
export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;

  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }

  const rounded = unit === 0 || value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${UNITS[unit]}`;
}

/** "22 Jul 2026", per the duplicate prompt in the handoff. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";

  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
}

/** "Weekly sync · 22 Jul 2026" — a source by its title and, when known, its date. */
export function withDate(title: string | null, date: string | null): string {
  const named = title ?? "a source";
  return date ? `${named} · ${formatDate(date)}` : named;
}

/**
 * "18m", "1h 04m" — how long a recording is, the way the recordings list writes it
 * (UI-4).
 *
 * Rounded to the minute, because nothing on that row is decided by seconds; a meeting
 * under a minute reads as "<1m" rather than "0m", which would look like a mistake.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "";

  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;

  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * "01:03", "1:04:22" — a position in a recording, as the transcript's mono timecode
 * writes it (UI-5).
 *
 * Different from `formatDuration` on purpose. That one rounds to the minute because
 * nothing on a list row turns on seconds; this one is a place in the audio somebody is
 * about to look for, so the seconds are the point. The hour is left off entirely below
 * an hour rather than shown as `00:` — most meetings never reach one.
 */
export function formatTimecode(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "";

  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = String(Math.floor(total / 60) % 60).padStart(2, "0");
  const seconds = String(total % 60).padStart(2, "0");

  return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}

/**
 * "11m 49s", "1h 02m" — how long something took, the way the recording detail's
 * "Pipeline complete" line writes it (UI-5).
 *
 * A third duration format, and each one exists because it is read for a different
 * reason. `formatDuration` says how long a meeting is, to the minute, on a list row.
 * `formatTimecode` says where in the audio something is, to the second. This one says
 * how long a job took — where the seconds matter under an hour and stop mattering
 * above it.
 */
export function formatElapsed(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "";

  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;

  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, "0")}s`;

  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * "$0.0123" — what a step's Anthropic call cost (ARCH-8), the way the Debug tab's header
 * writes it.
 *
 * Four decimal places rather than two: a single step's call is usually a few cents or
 * less, and rounding to the cent would show "$0.00" for most of them, which reads as
 * free rather than as small.
 */
export function formatCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return "";

  return `$${usd.toFixed(4)}`;
}

/**
 * "2 hours ago", "yesterday", "3 weeks ago" — how the world view says when something last
 * moved (UI-10).
 *
 * Relative rather than absolute because that is the question being asked on those lines:
 * "updated 2 hours ago" says the page is warm, where "updated 30 Aug 2026" makes the reader
 * do the subtraction. Above a year it stops pretending and prints the date, which is what
 * `Intl.RelativeTimeFormat` cannot decide for itself.
 */
export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "";

  const then = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(then.getTime())) return "";

  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 0) return "just now";
  if (seconds < 90) return "just now";

  const relative = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return relative.format(-minutes, "minute");

  const hours = Math.round(minutes / 60);
  if (hours < 24) return relative.format(-hours, "hour");

  const days = Math.round(hours / 24);
  if (days < 7) return relative.format(-days, "day");

  const weeks = Math.round(days / 7);
  if (weeks < 5) return relative.format(-weeks, "week");

  const months = Math.round(days / 30);
  if (months < 12) return relative.format(-months, "month");

  return formatDate(value);
}

/**
 * "10:32" — a clock time, the way UI-23's "Up to date · last updated 10:32" writes it.
 * Today's date has already been said by the moment somebody reads this line, so unlike
 * `formatRelative` it never needs to fall back to a date.
 */
export function formatTime(value: string | null | undefined): string {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "2-digit", hour12: false }).format(date);
}

/**
 * "10:32" — the wall-clock moment `offsetMs` into a recording that started at `startedAt`,
 * for the meetings table and Transcript Review, which want to say when something was said
 * rather than how far into the recording it was.
 *
 * Null rather than a fallback string when `startedAt` is unknown (a recording filed
 * before 0066 added it, or a browser upload, which has no such moment to report) — the
 * caller decides what to show instead, typically `formatTimecode`'s elapsed position.
 */
export function formatClockAt(
  startedAt: string | null | undefined,
  offsetMs: number | null | undefined,
): string | null {
  if (!startedAt || offsetMs === null || offsetMs === undefined || offsetMs < 0) return null;

  const start = new Date(startedAt);
  if (Number.isNaN(start.getTime())) return null;

  return formatTime(new Date(start.getTime() + offsetMs).toISOString());
}

/** Today, as the `yyyy-MM-dd` a date input and the Server both expect. */
export function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
